import React from 'react';

import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { BrowseAgentsModal } from '@atlassian/conversation-assistant-agent';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { type EditorAgent, getEditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import { type EditorPluginAIConfigItemMarkdown } from '../../../config-items/config-items';
import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import { openBrowseAgentsModal } from '../../../pm-plugins/rovo-agents/commands';
import { createAgentConfigItem } from '../../../prebuilt/config-item-agents/create-agent-config-item';
import { type AIGlobalOptIn, type EditorPluginAIProvider } from '../../../types';

export const BrowseRovoAgentsModal = ({
	editorView,
	aiGlobalOptIn,
	editorPluginAIProvider,
	startingConfig,
}: {
	editorView: EditorView;
	aiGlobalOptIn: AIGlobalOptIn;
	editorPluginAIProvider?: EditorPluginAIProvider;
	startingConfig: EditorPluginAIConfigItemMarkdown;
}) => {
	const [cloudId, setCloudId] = React.useState('');
	const [selectedAgent, setSelectedAgent] = React.useState<EditorAgent>();

	React.useEffect(() => {
		const id = editorPluginAIProvider?.getChannelVariables?.('rovo-agent')?.cloudId || '';
		setCloudId(id);
	}, [editorPluginAIProvider]);

	const openAIModalWithAgent = React.useCallback(
		(agent: EditorAgent, prompt?: string) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state: editorView.state,
				// the startingConfig can be undefined if the modal has been triggered from the floating toolbar
				// in that case we default to 'range' selectionType as the toolbar is only available on range selection
				configItem: createAgentConfigItem(startingConfig?.selectionType || 'range', agent),
				lastTriggeredFrom: 'floatingToolbarDropdown',
				aiGlobalOptIn,
				initialPrompt: prompt,
			});

			openAIModalCommand(editorView.state, editorView.dispatch);
		},
		[aiGlobalOptIn, editorView, startingConfig?.selectionType],
	);

	React.useEffect(() => {
		if (selectedAgent) {
			openAIModalWithAgent(selectedAgent);
		}
	}, [selectedAgent, openAIModalWithAgent]);

	if (!fg('platform_editor_ai_use_browse_rovo_agents_modal')) {
		return null;
	}

	const handleCloseModal = () =>
		openBrowseAgentsModal(false)(editorView.state, editorView.dispatch);

	const handleOnConversationStarterClick = (agent: Agent, prompt: string) => {
		const selectedAgent = getEditorAgent(agent);
		openAIModalWithAgent(selectedAgent, prompt);
		handleCloseModal();
	};

	const handleOnClickChat = (agent: Agent) => {
		setSelectedAgent(getEditorAgent(agent));
		handleCloseModal();
	};

	return (
		<BrowseAgentsModal
			onClose={handleCloseModal}
			siteId={cloudId}
			onClickChat={handleOnClickChat}
			handleClickConversationStarter={handleOnConversationStarterClick}
		/>
	);
};
